import React, { useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { BlockTyp, DEFAULT_PIKTO_KEYS, NotrufplanInhalt, SigeBlock } from './types';
import { MUSTER_LEGENDE, MUSTER_LMRA_FAKTOREN, MUSTER_LMRA_REGELN } from './musterVorlage';
import { ermittleNotrufplan } from './notrufAuto';
import { Contact } from '../../types';
import { Button } from '../Button';
import { CheckSquare, Loader2, MapPin, Square } from 'lucide-react';

// Schritt 5 des Anlage-Assistenten: Rand-Blöcke des Plans. Vorausgewählt sind
// die vier, die im Muster-Plan immer stehen. Bereits vorhandene Blöcke werden
// nicht erneut angelegt.

interface Props {
  sigeProjektId: string;
  bloecke: SigeBlock[];
  adresse?: string;
  beteiligte: Contact[];
  onGeaendert: () => void | Promise<void>;
  melde: (text: string, ton?: 'info' | 'fehler') => void;
}

interface BlockVorlage {
  typ: BlockTyp;
  titel: string;
  hinweis: string;
  inhalt: () => any;
}

// Standard-Inhalte wie im Block-Editor — so ist ein Block sofort druckreif.
const VORLAGEN: BlockVorlage[] = [
  { typ: 'notrufplan', titel: 'Notrufplan', hinweis: 'Krankenhaus, Durchgangsarzt, Augenarzt, Apotheke, Ersthelfer', inhalt: () => ({}) },
  { typ: 'legende', titel: 'Legende', hinweis: `Abkürzungen der Vorschriften (${MUSTER_LEGENDE.length} Einträge)`, inhalt: () => ({ eintraege: MUSTER_LEGENDE }) },
  { typ: 'piktogrammfeld', titel: 'Gebots-/Warnzeichen', hinweis: 'ISO-7010-Zeichen für den Planrand', inhalt: () => ({ icons: [...DEFAULT_PIKTO_KEYS] }) },
  { typ: 'risk_assessment', titel: 'Last Minute Risk Assessment', hinweis: 'LMRA-Faktoren und -Regeln', inhalt: () => ({ zeilen: [...MUSTER_LMRA_FAKTOREN, '', ...MUSTER_LMRA_REGELN] }) },
];

export const WizardStepBloecke: React.FC<Props> = ({ sigeProjektId, bloecke, adresse, beteiligte, onGeaendert, melde }) => {
  const vorhanden = (typ: BlockTyp) => bloecke.find(b => b.typ === typ);
  // Ohne Baumassnahme-Adresse blieb der Notrufplan hier ganz ohne Karte. Zum
  // Verorten genuegt jede Adresse in der Naehe; sie wird im Block vermerkt und
  // auf dem Plan unter der Karte ausgewiesen.
  const [ersatzAdresse, setErsatzAdresse] = useState(
    () => {
      const b = bloecke.find(x => x.typ === 'notrufplan');
      const i: NotrufplanInhalt = (b?.inhalt && typeof b.inhalt === 'object') ? b.inhalt : {};
      return i.ersatzAdresse || '';
    },
  );
  const suchAdresse = (adresse?.trim() || ersatzAdresse.trim());
  const nutztErsatz = !adresse?.trim() && !!ersatzAdresse.trim();

  const [auswahl, setAuswahl] = useState<Set<BlockTyp>>(
    () => new Set(VORLAGEN.filter(v => !bloecke.some(b => b.typ === v.typ)).map(v => v.typ)),
  );
  const [speichert, setSpeichert] = useState(false);
  const [notrufLaeuft, setNotrufLaeuft] = useState(false);
  const [notrufStatus, setNotrufStatus] = useState<string | null>(null);

  const umschalten = (typ: BlockTyp) => {
    if (vorhanden(typ)) return;
    setAuswahl(prev => {
      const next = new Set(prev);
      if (next.has(typ)) next.delete(typ); else next.add(typ);
      return next;
    });
  };

  const anlegen = async () => {
    const fehlende = VORLAGEN.filter(v => auswahl.has(v.typ) && !vorhanden(v.typ));
    if (fehlende.length === 0) {
      melde('Alle gewählten Blöcke sind bereits vorhanden.', 'fehler');
      return;
    }
    setSpeichert(true);
    try {
      const { error } = await supabase.from('sige_bloecke').insert(
        fehlende.map(v => ({ sige_projekt_id: sigeProjektId, typ: v.typ, titel: v.titel, inhalt: v.inhalt() })),
      );
      if (error) throw error;
      melde(`${fehlende.length} Block/Blöcke angelegt.`);
      await onGeaendert();
    } catch (e: any) {
      console.error('Blöcke anlegen fehlgeschlagen', e);
      melde(`Blöcke konnten nicht angelegt werden: ${e?.message || String(e)}`, 'fehler');
    } finally {
      setSpeichert(false);
    }
  };

  /**
   * Nutzt denselben Ablauf wie der Notrufplan-Editor (notrufAuto.ts). Ein schon
   * vorhandener Block wird ergänzt, nicht ersetzt — von Hand erfasste Ersthelfer
   * bleiben stehen.
   */
  const notrufBefuellen = async () => {
    if (!suchAdresse) {
      setNotrufStatus('Keine Adresse — bitte eine Adresse in der Nähe der Baustelle eintragen.');
      return;
    }
    setNotrufLaeuft(true);
    setNotrufStatus('Adresse wird lokalisiert …');
    try {
      const ergebnis = await ermittleNotrufplan(suchAdresse, setNotrufStatus);
      const block = vorhanden('notrufplan');
      const alt: NotrufplanInhalt = (block?.inhalt && typeof block.inhalt === 'object') ? block.inhalt : {};
      const ersthelfer = alt.ersthelfer?.length
        ? alt.ersthelfer
        : beteiligte.slice(0, 5).map(c => ({ name: c.name, firma: c.company || undefined }));
      const inhalt: NotrufplanInhalt = {
        ...alt,
        ...ergebnis.kontakte,
        ersthelfer,
        kartenBild: ergebnis.kartenBild ?? alt.kartenBild,
        geo: ergebnis.geo,
        ersatzAdresse: nutztErsatz ? ersatzAdresse.trim() : alt.ersatzAdresse,
      };

      if (block) {
        const { error } = await supabase.from('sige_bloecke').update({ inhalt }).eq('id', block.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('sige_bloecke')
          .insert({ sige_projekt_id: sigeProjektId, typ: 'notrufplan', titel: 'Notrufplan', inhalt });
        if (error) throw error;
      }
      setNotrufStatus(nutztErsatz
        ? `${ergebnis.status} ⚠ Karte und Entfernungen beziehen sich auf die Ersatzadresse „${ersatzAdresse.trim()}", nicht auf die Baustelle.`
        : ergebnis.status);
      await onGeaendert();
    } catch (e: any) {
      console.error('Notrufplan-Ermittlung fehlgeschlagen', e);
      setNotrufStatus(e instanceof Error ? e.message : 'Ermittlung fehlgeschlagen.');
    } finally {
      setNotrufLaeuft(false);
    }
  };

  const offene = VORLAGEN.filter(v => auswahl.has(v.typ) && !vorhanden(v.typ)).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Die Blöcke stehen im Randbereich des Plans. Vorausgewählt ist, was im Musterplan immer
        enthalten ist; Inhalte lassen sich später im Editor feinschleifen.
      </p>

      <div className="space-y-2">
        {VORLAGEN.map(v => {
          const da = vorhanden(v.typ);
          const an = !!da || auswahl.has(v.typ);
          return (
            <div
              key={v.typ}
              onClick={() => umschalten(v.typ)}
              className={`border rounded-xl p-3 flex items-start gap-3 transition-colors ${
                da ? 'bg-emerald-50/50 border-emerald-200' : an ? 'bg-blue-50 border-blue-300 cursor-pointer' : 'bg-white border-slate-200 hover:border-slate-300 cursor-pointer'
              }`}
            >
              <div className="mt-0.5 shrink-0">
                {an ? <CheckSquare className={`w-5 h-5 ${da ? 'text-emerald-600' : 'text-blue-600'}`} /> : <Square className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{v.titel}</div>
                <div className="text-xs text-slate-500 mt-0.5">{v.hinweis}</div>
              </div>
              {da && <span className="text-[11px] text-emerald-700 shrink-0">bereits im Plan</span>}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button variant="primary" onClick={anlegen} disabled={speichert || offene === 0}>
          {speichert && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Fehlende Blöcke anlegen ({offene})
        </Button>
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-50/70 border border-blue-100 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="min-w-[14rem]">
            <div className="text-sm font-semibold text-slate-800">Notrufplan automatisch befüllen</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Sucht Krankenhaus, Durchgangsarzt, Augenarzt und Apotheke zur Baustellen-Adresse
              (OpenStreetMap) und erzeugt den Kartenausschnitt.
            </div>
          </div>
          <Button variant="ai" onClick={notrufBefuellen} disabled={notrufLaeuft || !suchAdresse} className="text-sm shrink-0">
            {notrufLaeuft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
            Stellen ermitteln
          </Button>
        </div>
        {!adresse?.trim() && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-2">
            <div className="text-xs text-amber-800">
              Keine Baumaßnahme-Adresse hinterlegt. Für Suche und Karte genügt eine <b>Adresse in der Nähe</b>
              {' '}(Nachbargebäude, Kreuzung, Ortsmitte).
            </div>
            <input
              className="w-full text-sm border border-amber-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="z. B. Hauptstraße 1, 21079 Hamburg"
              value={ersatzAdresse}
              onChange={e => setErsatzAdresse(e.target.value)}
            />
          </div>
        )}
        {notrufStatus && (
          <div className="text-xs text-indigo-900 bg-white/70 border border-indigo-100 rounded-lg px-2.5 py-2">{notrufStatus}</div>
        )}
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Automatisch ermittelte Nummern und Adressen vor dem Druck prüfen — OSM-Daten sind lückenhaft,
          fehlende Telefonnummern werden per KI geschätzt.
        </div>
      </div>
    </div>
  );
};
